import type { XYPosition } from "@xyflow/react";
import { GROUP_PADDING } from "@/utils/group-utils";
import {
  DEFAULT_NODE_HEIGHT,
  DEFAULT_NODE_WIDTH,
} from "@/components/Spotflow/Editor/EditorUtils";

/** Room reserved above the children for the group's title strip; matches the
 *  extra top allowance `groupSelectedNodesV2Atom` uses when framing a group. */
export const GROUP_LABEL_HEIGHT = 20;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface GroupFrame {
  position: XYPosition;
  width: number;
  height: number;
}

export interface PositionedNode {
  id: string;
  type?: string;
  parentId?: string;
  position: XYPosition;
  width?: number;
  height?: number;
  measured?: { width?: number; height?: number };
  style?: { width?: number | string; height?: number | string };
}

export function isGroupReactFlowNode(
  node: { type?: string } | undefined
): boolean {
  return node?.type === "groupNodeV2";
}

export function absoluteNodePosition<T extends PositionedNode>(
  node: T,
  byId: Map<string, T>
): XYPosition {
  let x = node.position.x;
  let y = node.position.y;
  let parentId = node.parentId;
  while (parentId) {
    const parent = byId.get(parentId);
    if (!parent) break;
    x += parent.position.x;
    y += parent.position.y;
    parentId = parent.parentId;
  }
  return { x, y };
}

function styleLength(value: number | string | undefined): number | undefined {
  return typeof value === "number" ? value : undefined;
}

/** The node's on-canvas size: the live measurement first, then the explicit
 *  size props, then the editor's default node footprint. */
export function nodeSize(
  node: PositionedNode,
  fallback: { width: number; height: number } = {
    width: DEFAULT_NODE_WIDTH,
    height: DEFAULT_NODE_HEIGHT,
  }
): { width: number; height: number } {
  return {
    width:
      node.measured?.width ??
      node.width ??
      styleLength(node.style?.width) ??
      fallback.width,
    height:
      node.measured?.height ??
      node.height ??
      styleLength(node.style?.height) ??
      fallback.height,
  };
}

export function groupFrame(group: PositionedNode): GroupFrame {
  return { position: group.position, ...nodeSize(group) };
}

/** Groups are always top-level containers, so their position is absolute. */
export function groupContainingPoint<T extends PositionedNode>(
  nodes: T[],
  point: XYPosition
): T | undefined {
  return nodes
    .filter((n) => isGroupReactFlowNode(n))
    .map((group) => ({ group, ...groupFrame(group) }))
    .filter(
      (g) =>
        point.x >= g.position.x &&
        point.x <= g.position.x + g.width &&
        point.y >= g.position.y &&
        point.y <= g.position.y + g.height
    )
    .sort((a, b) => a.width * a.height - b.width * b.height)[0]?.group;
}

export function parentGroupOf<T extends PositionedNode>(
  nodeId: string,
  nodes: T[]
): T | undefined {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node?.parentId) return undefined;
  const parent = nodes.find((n) => n.id === node.parentId);
  return isGroupReactFlowNode(parent) ? parent : undefined;
}

export function groupContainsRect(frame: GroupFrame, rect: Rect): boolean {
  return (
    rect.x >= frame.position.x &&
    rect.y >= frame.position.y &&
    rect.x + rect.width <= frame.position.x + frame.width &&
    rect.y + rect.height <= frame.position.y + frame.height
  );
}

/** The smallest frame that keeps the group's current area and also wraps
 *  `rect` (absolute coordinates) with the standard group padding. Grows in
 *  any direction; `null` when the frame is already large enough. */
export function expandGroupFrameToContain(
  frame: GroupFrame,
  rect: Rect
): GroupFrame | null {
  const left = Math.min(frame.position.x, rect.x - GROUP_PADDING);
  const top = Math.min(
    frame.position.y,
    rect.y - GROUP_PADDING - GROUP_LABEL_HEIGHT
  );
  const right = Math.max(
    frame.position.x + frame.width,
    rect.x + rect.width + GROUP_PADDING
  );
  const bottom = Math.max(
    frame.position.y + frame.height,
    rect.y + rect.height + GROUP_PADDING
  );
  const next: GroupFrame = {
    position: { x: left, y: top },
    width: right - left,
    height: bottom - top,
  };
  const unchanged =
    next.position.x === frame.position.x &&
    next.position.y === frame.position.y &&
    next.width === frame.width &&
    next.height === frame.height;
  return unchanged ? null : next;
}

/** Re-frame a group by mutating the array's nodes (an immer draft, or the
 *  copies made by `applyGroupFrame`). When the origin moves, existing
 *  children are shifted by the opposite delta so they stay put on canvas. */
export function reframeGroupInPlace(
  nodes: PositionedNode[],
  groupId: string,
  frame: GroupFrame
): void {
  const group = nodes.find((n) => n.id === groupId);
  if (!group) return;
  const shift = {
    x: group.position.x - frame.position.x,
    y: group.position.y - frame.position.y,
  };
  for (const n of nodes) {
    if (n.id === groupId) {
      n.position = { ...frame.position };
      n.width = frame.width;
      n.height = frame.height;
      n.style = { ...n.style, width: frame.width, height: frame.height };
    } else if (n.parentId === groupId && (shift.x !== 0 || shift.y !== 0)) {
      n.position = { x: n.position.x + shift.x, y: n.position.y + shift.y };
    }
  }
}

/** Immutable `reframeGroupInPlace`: only the group and, if its origin moves,
 *  its children get new objects — every other node keeps its identity. */
export function applyGroupFrame<T extends PositionedNode>(
  nodes: T[],
  groupId: string,
  frame: GroupFrame
): T[] {
  const group = nodes.find((n) => n.id === groupId);
  if (!group) return nodes;
  const originMoves =
    group.position.x !== frame.position.x ||
    group.position.y !== frame.position.y;
  const next = nodes.map((n) =>
    n.id === groupId || (originMoves && n.parentId === groupId) ? { ...n } : n
  );
  reframeGroupInPlace(next, groupId, frame);
  return next;
}

/** Grow each group whose listed child now overflows it — used after React
 *  Flow measures a child taller or wider than the frame that was reserved
 *  for it. Returns the same array when nothing overflows. */
export function growGroupsToFitChildren<T extends PositionedNode>(
  nodes: T[],
  childIds: Iterable<string>
): T[] {
  let result = nodes;
  for (const childId of childIds) {
    const child = result.find((n) => n.id === childId);
    if (!child?.parentId) continue;
    const group = result.find((n) => n.id === child.parentId);
    if (!group || !isGroupReactFlowNode(group)) continue;
    const frame = groupFrame(group);
    const rect: Rect = {
      x: frame.position.x + child.position.x,
      y: frame.position.y + child.position.y,
      ...nodeSize(child),
    };
    if (groupContainsRect(frame, rect)) continue;
    const next = expandGroupFrameToContain(frame, rect);
    if (next) result = applyGroupFrame(result, group.id, next);
  }
  return result;
}
